/**
 * The owner sign-in ("activation") badge — ADR: 1toall-portal-api docs/ACTIVATION-BADGE-ADR.md §Display.
 * Behaviour lives in lib/activationStatus.ts; this file only chooses a tone and renders.
 *
 * THREE TEXTS (owner decision 14/9 night), identical in Thai and English: "Activate: Activated" (ok),
 * "Activate: Pending" (info — needs_checking on a watched hot/warm tier) and "Activate: Unknown" (neutral —
 * needs_checking on roster/unknown tier, no_data, or anything unrecognised). The visible text names what
 * the pill is, so there is no visually-hidden prefix. NEVER danger and never warn: a missing sign-in is
 * missing evidence, not a fault, and "Pending" is not a verdict. StatusPill renders a dot AND text, so
 * colour is never the only signal.
 *
 * NOT UPDATING. When the pipeline behind the badge has gone quiet (API `stale`, or the caller's refetch is
 * failing) the label stays but the tone drops to neutral and the caption says so. A green pill nobody is
 * refreshing would vouch for a signal that stopped moving.
 *
 * ITS OWN TIME. The caption prints the badge's own checked_at, never a page-level freshness stamp — the
 * badge moves every few seconds, the mirror behind the rest of the page does not.
 *
 * FAIL-SAFE. `activation` may be absent (API rolled back → 404, or a field missing) → "Activate: Unknown".
 */

import { useTranslation } from "react-i18next";
import { StatusPill, type StatusTone } from "@/components/ui";
import { activationViewOf, type ActivationDisplay } from "@/lib/activationStatus";
import { formatInstant } from "@/lib/format";
import { cn } from "@/lib/cn";
import type { CustomerActivation } from "@/api/types";

const TONE: Record<ActivationDisplay, StatusTone> = {
  activated: "ok",
  pending: "info",
  unknown: "neutral",
};

/** Static key maps (not asTKey): every variant's copy is a compile-checked literal key. */
const LABEL = {
  activated: "activation.state.activated",
  pending: "activation.state.pending",
  unknown: "activation.state.unknown",
} as const satisfies Record<ActivationDisplay, string>;

const HINT = {
  activated: "activation.hint.activated",
  pending: "activation.hint.pending",
  unknown: "activation.hint.unknown",
} as const satisfies Record<ActivationDisplay, string>;

export interface ActivationBadgeProps {
  /** Absent / partial → normalised by activationViewOf (unknown state → "Activate: Unknown"). */
  activation?: CustomerActivation | null | undefined;
  /** The caller's last refetch failed while older data is on screen → treat as not updating. */
  refetchFailing?: boolean | undefined;
  className?: string | undefined;
}

export function ActivationBadge({
  activation,
  refetchFailing = false,
  className,
}: ActivationBadgeProps) {
  const { t } = useTranslation();
  const view = activationViewOf(activation, refetchFailing);
  const tone: StatusTone = view.notUpdating ? "neutral" : TONE[view.display];

  const time = view.checkedAt ? formatInstant(view.checkedAt) : null;
  const caption = view.notUpdating
    ? time
      ? t("activation.notUpdatingSince", { time })
      : t("activation.notUpdating")
    : time
      ? t("activation.checkedAt", { time })
      : null;

  return (
    <span className={cn("inline-flex flex-wrap items-center gap-x-2 gap-y-1", className)}>
      <StatusPill tone={tone} title={t(HINT[view.display])}>
        {t(LABEL[view.display])}
      </StatusPill>
      {caption && <span className="text-(length:--text-xs) text-(--text-muted)">{caption}</span>}
    </span>
  );
}
